import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { connect } from "react-redux";

import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { ReactComponent as PriceIcon } from "feather-icons/dist/icons/dollar-sign.svg";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as StarIcon } from "feather-icons/dist/icons/star.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import { fetchBlogPostsData } from "services/backendCalls";
import { APP_NAME } from "../../constants";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-16 lg:py-20`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-16`}
  .slick-track { 
    ${tw`flex items-stretch`}
  }
  .slick-slide {
    ${tw`h-auto flex mb-1`}
    display: flex !important;
    justify-content: center;
    align-items: stretch;
  }
  .slick-slide > div {
    ${tw`w-full`}
  }
  .slick-list {
    ${tw`px-1 sm:px-0`}
  }
`;
const Slide = styled.div`
  ${tw`px-2 h-full w-full`}
  display: flex;
  width: 100%;
`;
const Card = styled.div`
  ${tw`w-full max-w-sm mx-auto h-full flex flex-col overflow-hidden sm:border sm:rounded-tl-4xl sm:rounded-br-5xl relative focus:outline-none`}
  min-width: 0;
  width: 100%;
  flex: 1 1 100%;
`;
const CardImage = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`w-full h-48 sm:h-56 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`
]);

const TextInfo = tw.div`flex-1 py-6 sm:px-10 sm:py-6`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const Title = styled.h5`
  ${tw`text-xl sm:text-2xl font-bold leading-tight`}
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const RatingsInfo = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500 fill-current`}
  }
`;
const Rating = tw.span`ml-2 font-bold`;

const Description = styled.p`
  ${tw`text-sm leading-7 mt-2 sm:mt-4`}
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const SecondaryInfoContainer = tw.div`flex flex-col flex-wrap sm:flex-row mt-2 sm:mt-4`;
const IconWithText = tw.div`flex items-start mr-6 my-2 sm:my-0 max-w-full`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full p-2 bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;
const Text = styled.div`
  ${tw`ml-2 text-sm font-semibold text-gray-800`}
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const PrimaryButton = tw(PrimaryButtonBase)`mt-auto sm:text-lg rounded-none w-full rounded sm:rounded-none sm:rounded-br-4xl py-3 sm:py-6`;

const EmptyState = tw.div`text-center py-16`;
const EmptyIcon = tw.div`text-6xl mb-4`;
const EmptyTitle = tw.h3`text-2xl font-bold text-gray-700 mb-2`;
const EmptyText = tw.p`text-gray-500`;

const BlogComponent = (props) => {
  const [sliderRef, setSliderRef] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const heading = props.BlogData.heading;

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        const fetchedPosts = await fetchBlogPostsData();
        setPosts(Array.isArray(fetchedPosts) ? fetchedPosts : []);
      } catch (e) {
        console.error('Failed to load blog posts:', e);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  const cards = posts.map((card) => ({
    imageSrc: card.imageSrc || card.image || "",
    title: card.title || card.heading || "Untitled post",
    rating: card.rating || "New",
    locationText: card.locationText || card.author || APP_NAME,
    pricingText: card.pricingText || card.category || "Blog",
    description: card.description || card.excerpt || "",
    link: card.link || card.url || "https://bll-wi.blogspot.com"
  }));

  const sliderSettings = {
    ...(props.BlogData?.sliderSettings || {}),
    infinite: cards.length > 1,
    slidesToShow: Math.min(cards.length || 1, props.BlogData?.sliderSettings?.slidesToShow || 3),
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(cards.length || 1, 2),
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) {
    return (
      <Container id="blog">
        <Content>
          <HeadingWithControl>
            <Heading>{heading}</Heading>
          </HeadingWithControl>
          <div className="text-center py-12">
            <div className="animate-spin inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full" />
            <p className="mt-2 text-gray-500">Loading blog posts...</p>
          </div>
        </Content>
      </Container>
    );
  }

  if (cards.length === 0) {
    return (
      <Container id="blog">
        <Content>
          <HeadingWithControl>
            <Heading>{heading}</Heading>
          </HeadingWithControl>
          <EmptyState>
            <EmptyIcon><span role="img" aria-label="blog posts">📝</span></EmptyIcon>
            <EmptyTitle>No Blog Posts Yet</EmptyTitle>
            <EmptyText>Blog posts will appear here once they're published from the admin dashboard.</EmptyText>
          </EmptyState>
        </Content>
      </Container>
    );
  }

  return (
    <Container id="blog">
      <Content>
        <HeadingWithControl>
          <Heading>{heading}</Heading>
          <Controls>
            <PrevButton onClick={() => sliderRef?.slickPrev?.()}><ChevronLeftIcon/></PrevButton>
            <NextButton onClick={() => sliderRef?.slickNext?.()}><ChevronRightIcon/></NextButton>
          </Controls>
        </HeadingWithControl>
        <CardSlider ref={setSliderRef} {...sliderSettings}>
          {cards.map((card, index) => (
            <Slide key={index}>
              <Card>
                <CardImage imageSrc={card.imageSrc} />
                <TextInfo>
                  <TitleReviewContainer>
                    <Title>{card.title}</Title>
                    <RatingsInfo>
                      <StarIcon />
                      <Rating>{card.rating}</Rating>
                    </RatingsInfo>
                  </TitleReviewContainer>
                  <SecondaryInfoContainer>
                    <IconWithText>
                      <IconContainer>
                        <LocationIcon />
                      </IconContainer>
                      <Text>{card.locationText}</Text>
                    </IconWithText>
                    <IconWithText>
                      <IconContainer>
                        <PriceIcon />
                      </IconContainer>
                      <Text>{card.pricingText}</Text>
                    </IconWithText>
                  </SecondaryInfoContainer>
                  <Description>{card.description}</Description>
                </TextInfo>
                <PrimaryButton as="a" href={card.link || "https://bll-wi.blogspot.com"} target="_blank" rel="noreferrer">Read More...</PrimaryButton>
              </Card>
            </Slide>
          ))}
        </CardSlider>
      </Content>
    </Container>
  );
};


const Blog = connect((state) => ({
  BlogData: state.BlogData
}))(BlogComponent);

export default Blog;